import type { AdminCustomerCart, StoreCustomer } from "@hobbyshopify/backend-client";
import { Fragment, type ReactNode, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { AppPrompt } from "../../components/AppPrompt";
import { useSaidInView } from "../../components/BottomBar";
import { CustomerTabs } from "../../components/CustomerTabs";
import { ImageLightbox } from "../../components/ImageLightbox";
import { LoadFailed, Loading } from "../../components/LoadState";
import { ConfirmDialog, useModalLayer } from "../../components/ModalLayer";
import { ReviewGroupRow, ReviewSummary } from "../../components/ReviewGroupRow";
import { CameraIcon as ScanCameraIcon } from "../../components/review-icons";
import { ScanningScanRow } from "../../components/ScanningScanRow";
import { CustomerScanRow } from "../../components/ScanRow";
import { UnrecognisedScan } from "../../components/UnrecognisedScan";
import { useFocusOutlivesRows } from "../../components/use-focus-outlives-rows";
import { formatCents, scanPhotos } from "../../core/primitives/card-display";
import {
  canAddToCart,
  cartLineForScan,
  checkoutBarCents,
  checkoutCardCount,
} from "../../core/primitives/customer-cart";
import { customerInitial, customerLabel } from "../../core/primitives/customer-label";
import { formatDate } from "../../core/primitives/format";
import { confirmedScans, customerRowKey, reviewEntry, reviewPriceText } from "../../core/primitives/review-row";
import { CustomerCartState } from "../../sections/customer-cart/customer-cart-controller";
import { CartPayment, CustomerCartTile } from "../../sections/customer-cart/customer-cart-tile";
import {
  CustomerScansActions,
  CustomerScansState,
  customerListed,
  type NewCustomerDraft,
  unrecognisedOf,
} from "./customer-scans-model";

/** The customer's cart in place, over their scans, as iOS's Checkout: the Carts tab's tile, or word that it is gone. */
function CheckoutSheet({ cart }: { cart: AdminCustomerCart | null }) {
  const state = useSubtree(CustomerScansState);
  const actions = useSubtree(CustomerScansActions);
  const cartState = useSubtree(CustomerCartState);
  const observer = useObserver();
  const actionError = observer.watch(cartState.actionError);
  const cartStatus = observer.watch(state.cartStatus);
  // A change in Checkout is read back before it closes, as the controller holds it.
  const changing = observer.watch(cartState.busyItemId) !== null || observer.watch(cartState.busyCartId) !== null;
  const done = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const notes = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalLayer(done, actions.closeCheckout);
  // The sheet scrolls itself: what it says at its top comes into view, as a page's notes do. One hook per
  // sentence, so one going away does not bring the other, already shown, back into view.
  useSaidInView(notes, actionError, true, changing);
  useSaidInView(notes, cartStatus.kind === "error" ? cartStatus.message : null, true, changing);

  return createPortal(
    <div className="scrim items-end justify-center sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={actions.closeCheckout}
        className="absolute inset-0 cursor-default"
      />
      <div
        ref={sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="modal relative max-h-[90dvh] w-full max-w-[520px] overflow-y-auto p-4 outline-none sm:p-5"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold text-white">
            Checkout
          </h2>
          <button
            ref={done}
            type="button"
            onClick={actions.closeCheckout}
            disabled={changing}
            className="btn-ghost shrink-0 text-accent"
          >
            Done
          </button>
        </div>
        <div ref={notes}>
          {actionError && (
            <p role="alert" className="note note-danger mb-3 text-danger">
              {actionError}
            </p>
          )}
          {cartStatus.kind === "error" && (
            <div role="alert" className="note note-danger mb-3 flex items-center justify-between gap-3 text-danger">
              <span className="min-w-0">
                This cart could not be read again, so it may have changed. {cartStatus.message}
              </span>
              {/* The button goes while the cart is read, so the focus waits on the sheet. */}
              <button
                type="button"
                onClick={() => {
                  sheet.current?.focus();
                  actions.retry();
                }}
                className="btn-ghost shrink-0"
              >
                Read again
              </button>
            </div>
          )}
        </div>
        {cart ? (
          <CustomerCartTile cart={cart} />
        ) : (
          <div role="status" className="py-10 text-center">
            <p className="mb-1 text-base font-medium text-white">This cart is gone</p>
            <p className="text-sm text-muted">It was deleted, or its last card was removed.</p>
          </div>
        )}
      </div>
      <CartPayment />
    </div>,
    document.body,
  );
}

function CustomerRow({
  customer,
  cartCents,
  onSelect,
}: {
  customer: StoreCustomer;
  /** Their open carts, summed; undefined when they have none. */
  cartCents: number | undefined;
  onSelect: () => void;
}) {
  const label = customerLabel(customer);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 rounded-row border border-line bg-surface p-4 text-left transition-colors hover:border-accent"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-strong text-sm font-semibold text-white">
        {customerInitial(customer)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-white">{label}</p>
      </div>
      {cartCents !== undefined && (
        <span className="shrink-0 rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-accent">
          {`Cart ${formatCents(cartCents)}`}
        </span>
      )}
      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold text-white">
          {customer.scanCount} {customer.scanCount === 1 ? "scan" : "scans"}
        </p>
        <p className="text-xs text-faint">{formatDate(customer.lastScanAt)}</p>
      </div>
      <span className="shrink-0 text-lg text-faint">›</span>
    </button>
  );
}

function Empty({ icon, title, message }: { icon: ReactNode; title: string; message?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <span className="text-faint" aria-hidden="true">
        {icon}
      </span>
      <p className="text-sm font-semibold text-white">{title}</p>
      {message && <p className="max-w-xs text-xs text-muted">{message}</p>}
    </div>
  );
}

const PeopleIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-9 w-9">
    <circle cx="9" cy="8" r="3.2" />
    <path
      d="M3 19c.6-3.2 3-5 6-5s5.4 1.8 6 5M16 5.2a3 3 0 0 1 0 5.6M18 14.4c1.6.7 2.6 2.3 3 4.6"
      strokeLinecap="round"
    />
  </svg>
);

const CameraIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-9 w-9">
    <path d="M4 8.5h3l1.5-2h7l1.5 2h3v10H4z" strokeLinejoin="round" />
    <circle cx="12" cy="13.2" r="3.3" />
  </svg>
);

const StackIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-9 w-9">
    <rect x="6" y="4" width="12" height="16" rx="2" />
    <path d="M3.5 7v10M20.5 7v10" strokeLinecap="round" />
  </svg>
);

function NewCustomerDialog({ draft }: { draft: NewCustomerDraft }) {
  const actions = useSubtree(CustomerScansActions);
  const name = useRef<HTMLInputElement>(null);
  const titleId = useId();
  useModalLayer(name, actions.closeNewCustomer);

  return createPortal(
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={actions.closeNewCustomer}
        className="absolute inset-0 cursor-default"
      />
      <form
        className="modal relative w-full max-w-sm p-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(e) => {
          e.preventDefault();
          void actions.createCustomer();
        }}
      >
        <h2 id={titleId} className="text-base font-semibold text-white">
          Add customer
        </h2>
        <p className="mt-1 text-xs text-muted">Scan and check out a customer directly from the store.</p>
        <label className="label mt-4" htmlFor="new-customer-name">
          Name
        </label>
        <input
          ref={name}
          id="new-customer-name"
          className={`field ${draft.error ? "field-error" : ""}`}
          value={draft.name}
          onChange={(e) => actions.setNewCustomerName(e.target.value)}
          readOnly={draft.busy}
          autoComplete="off"
          autoCapitalize="words"
          aria-invalid={draft.error !== null}
          aria-describedby={draft.error ? "new-customer-error" : undefined}
        />
        {draft.error && (
          <p id="new-customer-error" role="alert" className="mt-1 text-xs text-danger">
            {draft.error}
          </p>
        )}
        {draft.failure && (
          <p role="alert" className="note note-danger mt-4 text-danger">
            {draft.failure}
          </p>
        )}
        {/* Held, not disabled, while the customer is made: the focus stays on the button pressed. */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={actions.closeNewCustomer}
            aria-disabled={draft.busy || undefined}
            className="btn-secondary btn-sm aria-disabled:cursor-default aria-disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            aria-disabled={draft.busy || undefined}
            className="btn-primary btn-sm aria-disabled:cursor-default aria-disabled:opacity-50"
          >
            {draft.busy ? "Adding…" : "Start sale"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

/** What an action answered: a refusal or a failure, with Dismiss. */
function ActionSaid({
  text,
  onDismiss,
  className = "",
  inView = false,
}: {
  text: string;
  onDismiss: () => void;
  className?: string;
  inView?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useSaidInView(ref, inView ? text : null, true, false);
  return (
    <div
      ref={ref}
      className={`note note-danger flex items-center justify-between gap-3 text-danger ${className}`}
      role="alert"
    >
      <span className="min-w-0">{text}</span>
      <button type="button" onClick={onDismiss} className="btn-ghost shrink-0">
        Dismiss
      </button>
    </div>
  );
}

export function CustomerScansPage() {
  const state = useSubtree(CustomerScansState);
  const actions = useSubtree(CustomerScansActions);
  const observer = useObserver();
  useFocusOutlivesRows();

  const selectedCustomerId = state.customerId;
  const storeId = observer.watch(state.storeId);
  const customersStatus = observer.watch(state.customersStatus);
  const customers = observer.watch(state.customers);
  const selectedCustomer = observer.watch(state.selectedCustomer);
  const scansStatus = observer.watch(state.scansStatus);
  const customerScans = observer.watch(state.scans);
  const openCarts = observer.watch(state.openCarts);
  const cart = observer.watch(state.cart);
  const captures = observer.watch(state.captures);
  const cartStatus = observer.watch(state.cartStatus);
  const busyScanId = observer.watch(state.busyScanId);
  const actionError = observer.watch(state.actionError);
  const actionErrorAt = observer.watch(state.actionErrorAt);
  const newCustomer = observer.watch(state.newCustomer);
  const deleting = observer.watch(state.deleting);
  const checkout = observer.watch(state.checkout);
  const reviewGroups = observer.watch(state.reviewGroups);
  const drafts = observer.watch(state.drafts);
  const busyKey = observer.watch(state.busyKey);
  const picking = observer.watch(state.picking);
  const queueFailed = observer.watch(state.queueFailed);
  const cartTotals = observer.watch(state.cartTotals);
  const [photos, setPhotos] = useState<string[] | null>(null);
  const carts = cart ? [cart] : [];
  const settledScans = confirmedScans(customerScans);
  const unrecognisedScans = unrecognisedOf(customerScans);
  const listed = customerListed(reviewGroups.length, customerScans);
  const pendingCount = reviewGroups.reduce((n, g) => n + g.count, 0);
  const checkoutBar = selectedCustomerId !== null && cart !== null && cart.items.length > 0;
  const cardCount = cart ? checkoutCardCount(cart) : 0;

  const pending = (kind: string) => kind === "idle" || kind === "loading";
  const loading =
    pending(customersStatus.kind) || (selectedCustomerId !== null && pending(scansStatus.kind)) || storeId === null;
  const error = customersStatus.kind === "error" || (selectedCustomerId !== null && scansStatus.kind === "error");
  // What a row's action answered is said under that row, where the focus stays; at the top once the row is gone.
  const rowKeys = new Set([...reviewGroups.map(customerRowKey), ...settledScans.map((s) => s.id)]);
  const rowsShown = selectedCustomerId !== null && !loading && !error;
  const saidAtRow = rowsShown && actionErrorAt !== null && rowKeys.has(actionErrorAt);
  const topSaid = saidAtRow ? null : actionError;
  const topNotes = useRef<HTMLDivElement>(null);
  useSaidInView(topNotes, topSaid, true, busyKey !== null || picking !== null || busyScanId !== null);
  // Brought into view, clear of the bars the root's scroll padding names, once the question that asked for the action
  // has closed and nothing is on its way: closing it moves the page.
  const settled = deleting === null && busyKey === null && picking === null && busyScanId === null;
  const saidUnder = (key: string) =>
    saidAtRow && actionErrorAt === key && actionError ? (
      <ActionSaid text={actionError} onDismiss={actions.dismissActionError} inView={settled} className="scroll-mt-12" />
    ) : null;

  const renderContent = () => {
    if (error) {
      const failed = customersStatus.kind === "error" ? customersStatus : scansStatus;
      return (
        <LoadFailed
          title={selectedCustomerId ? "Failed to load this customer's scans." : "Failed to load customers."}
          message={failed.kind === "error" ? failed.message : null}
          onRetry={actions.retry}
        />
      );
    }
    if (loading) return <Loading />;

    // Drilled into one customer: their scans, pending ones reviewable in place.
    if (selectedCustomerId) {
      if (!listed) {
        return selectedCustomer?.isPos ? (
          <Empty
            icon={CameraIcon}
            title="Scan this customer's cards"
            message="Review each scan, then add the ones they are buying to the cart."
          />
        ) : (
          <Empty icon={StackIcon} title="This customer has no valid scans." />
        );
      }
      return (
        <div className="space-y-2">
          {queueFailed && reviewGroups.length > 0 && (
            <div className="note note-warn flex items-center justify-between gap-3 text-warn" role="status">
              <span className="min-w-0">
                The review queue didn't load, so each scan below stands alone, priced from its own estimate.
              </span>
              <button type="button" onClick={actions.retry} className="btn-ghost shrink-0">
                Read again
              </button>
            </div>
          )}
          {reviewGroups.length > 0 && (
            // No "Confirm all priced" here: a customer's scan is a price question
            // that wants the reviewer's eye, as on Inventory › Review (`batchEntry`).
            <ReviewSummary detail={`${pendingCount} ${pendingCount === 1 ? "scan" : "scans"}`} pricedCount={0} />
          )}
          {/* The Inventory tab's row, without the count: a customer's scan is priced, never stocked. */}
          {reviewGroups.map((group) => {
            const key = customerRowKey(group);
            return (
              <Fragment key={key}>
                <ReviewGroupRow
                  group={group}
                  busy={busyKey === key || picking?.key === key}
                  price={reviewPriceText(group, drafts[key])}
                  quantity={0}
                  showQuantity={false}
                  canConfirm={reviewEntry(group, drafts[key]) !== null}
                  pickingProductId={picking?.key === key ? picking.productId : null}
                  onPrice={(price) => actions.setPrice(group, price)}
                  onQuantity={() => {}}
                  onPick={(productId) => void actions.pick(group, productId)}
                  onConfirm={() => void actions.confirm(group)}
                  onReview={() => actions.editScan(group.sample)}
                  onReject={() => void actions.reject(group)}
                  onOpenImage={() => setPhotos(scanPhotos(group.sample))}
                />
                {saidUnder(key)}
              </Fragment>
            );
          })}
          {unrecognisedScans.map((s) => (
            <UnrecognisedScan key={s.id} scan={s} />
          ))}
          {settledScans.map((s) => {
            const line = cartLineForScan(carts, s);
            const own = busyScanId === s.id;
            return (
              <Fragment key={s.id}>
                <CustomerScanRow
                  scan={s}
                  deleting={own && deleting?.id === s.id}
                  onOpen={() => actions.openScan(s)}
                  onDelete={() => actions.askDelete(s)}
                  cart={{
                    inCart: line !== null,
                    excluded: line !== null && !line.item.includedInPurchaseOrder,
                    canAdd: canAddToCart(s) && cartStatus.kind === "loaded",
                    canRemove: cartStatus.kind === "loaded",
                    busy: own && deleting?.id !== s.id,
                    onAdd: () => void actions.addToCart(s),
                    onRemove: () => void actions.removeFromCart(s),
                  }}
                />
                {saidUnder(s.id)}
              </Fragment>
            );
          })}
        </div>
      );
    }

    // Customer list.
    if (customers.length === 0) {
      return (
        <Empty
          icon={PeopleIcon}
          title="No customers with valid scans yet."
          message="Customers who scan cards in your shop show up here."
        />
      );
    }
    return (
      <div className="space-y-2">
        {customers.map((c) => (
          <CustomerRow
            key={c.id}
            customer={c}
            cartCents={cartTotals.get(c.id)}
            onSelect={() => actions.selectCustomer(c.id)}
          />
        ))}
      </div>
    );
  };

  // Where iOS has Quick Charge, which the browser cannot take: the app is a link away.
  const quickCharge = !selectedCustomerId && !loading && !error && (
    <div className="mt-6">
      <AppPrompt>Quick Charge, a sale for any amount with no scan, is in the HobbyScan app.</AppPrompt>
    </div>
  );

  // From 640 px the floating buttons' actions are in the head, where they cover nothing.
  const headAction = selectedCustomerId ? (
    selectedCustomer?.isPos && (
      <button
        type="button"
        onClick={actions.scanForCustomer}
        className="btn-primary btn-sm ml-auto hidden sm:inline-flex"
      >
        <ScanCameraIcon />
        Scan cards
      </button>
    )
  ) : (
    <button
      type="button"
      onClick={actions.openNewCustomer}
      disabled={!storeId}
      className="btn-primary btn-sm ml-auto hidden sm:inline-flex"
    >
      Add customer
    </button>
  );

  return (
    // Room under the list for the floating buttons on a phone, which ride above the Checkout bar when it shows, and
    // for the bar alone from 640 px.
    <div
      className={`mx-auto max-w-4xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8 ${checkoutBar ? "pb-40 sm:pb-28" : "pb-28 sm:pb-8"}`}
    >
      <div className="panel p-4 sm:p-6">
        <CustomerTabs active="scans" openCarts={openCarts} action={headAction} customer={selectedCustomerId} />

        {selectedCustomerId && (
          <div className="mb-4">
            <button
              type="button"
              onClick={actions.showAllCustomers}
              className="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-muted transition-colors hover:text-white"
            >
              ‹ All customers
              {selectedCustomer && (
                <span className="ml-1 truncate text-white">/ {customerLabel(selectedCustomer)}</span>
              )}
            </button>
          </div>
        )}

        <div ref={topNotes} className="scroll-mt-12 empty:hidden">
          {topSaid && <ActionSaid text={topSaid} onDismiss={actions.dismissActionError} className="mb-4" />}
        </div>

        {selectedCustomerId && cartStatus.kind === "error" && (
          <LoadFailed
            title="Couldn't load this customer's cart."
            message={cartStatus.message}
            onRetry={actions.retry}
          />
        )}

        {selectedCustomerId && captures.length > 0 && (
          // Above the empty state and the scans, as iOS's: the cards still on their way to this customer.
          <div className="mb-2 space-y-2">
            {captures.map((capture) => (
              <ScanningScanRow
                key={capture.id}
                capture={capture}
                onRetry={() => actions.retryCapture(capture.id)}
                onDiscard={() => actions.discardCapture(capture.id)}
              />
            ))}
          </div>
        )}
        {renderContent()}
        {quickCharge}
      </div>

      {/* On a phone, over the content at the thumb, as iOS's floating buttons are. */}
      {selectedCustomerId ? (
        selectedCustomer?.isPos && (
          <button
            type="button"
            onClick={actions.scanForCustomer}
            data-floating="button"
            className={`btn-primary fixed right-[max(1rem,env(safe-area-inset-right))] z-20 flex h-[52px] items-center gap-2 rounded-full px-5 shadow-lg sm:hidden ${
              checkoutBar ? "bottom-[calc(var(--tab-bar,0px)+6rem)]" : "bottom-[calc(var(--tab-bar,0px)+1.5rem)]"
            }`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path d="M4 8.5h3l1.5-2h7l1.5 2h3v10H4z" strokeLinejoin="round" />
              <circle cx="12" cy="13.2" r="3.3" />
            </svg>
            Scan cards
          </button>
        )
      ) : (
        <button
          type="button"
          onClick={actions.openNewCustomer}
          disabled={!storeId}
          aria-label="Add customer"
          data-floating="button"
          className="btn-primary fixed right-[max(1rem,env(safe-area-inset-right))] bottom-[calc(var(--tab-bar,0px)+1.5rem)] z-20 flex h-14 w-14 items-center justify-center rounded-full p-0 shadow-lg sm:hidden"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <circle cx="10" cy="8" r="3.2" />
            <path d="M4 19c.6-3.2 3-5 6-5 1.4 0 2.6.4 3.6 1.1M18 13v6M15 16h6" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {checkoutBar && cart && (
        // Fixed to the bottom, so the sale can be closed from wherever the list is scrolled to. From 640 px the button
        // stands under the list: the page's column, less the panel's border and padding.
        <div
          data-floating="checkout"
          className="fixed inset-x-0 bottom-[var(--tab-bar,0px)] z-10 border-t border-line bg-app-elevated/95 pt-3 pr-[max(1rem,env(safe-area-inset-right))] pl-4 pb-[max(0.75rem,calc(env(safe-area-inset-bottom)-var(--tab-bar,0px)))] backdrop-blur sm:left-52 sm:pr-[env(safe-area-inset-right)] sm:pl-0"
        >
          <div className="mx-auto max-w-4xl sm:px-[calc(3rem+1px)] lg:px-[calc(3.5rem+1px)]">
            <button
              type="button"
              onClick={actions.checkout}
              disabled={busyScanId !== null}
              className="btn-primary flex w-full items-center gap-2"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path
                  d="M2 3h2.2l1.9 9.2a1.6 1.6 0 0 0 1.6 1.3h7.4l1.6-6.5H5.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <circle cx="8" cy="16.6" r="1.3" />
                <circle cx="14.5" cy="16.6" r="1.3" />
              </svg>
              Checkout
              <span className="font-normal opacity-70">
                · {cardCount} {cardCount === 1 ? "card" : "cards"}
              </span>
              <span className="ml-auto">{formatCents(checkoutBarCents(cart))}</span>
            </button>
          </div>
        </div>
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete this scan?"
          message="Its price for the customer goes too, and it comes out of their cart."
          confirmLabel="Delete"
          busyLabel="Deleting…"
          cancelLabel="Cancel"
          busy={busyScanId === deleting.id}
          onConfirm={() => void actions.deleteScan()}
          onCancel={actions.cancelDelete}
        />
      )}
      {newCustomer && <NewCustomerDialog draft={newCustomer} />}
      {checkout && <CheckoutSheet cart={checkout.cart} />}
      {photos && <ImageLightbox urls={photos} onClose={() => setPhotos(null)} />}
    </div>
  );
}
